import { PIN_LENGTH } from '../../shared/constants.js';
import { buildPinPath } from './route.js';

/*
 * Contract between the page and the service worker for Web Share Target.
 * The worker cannot import modules (it is a classic script), so sw.ts repeats these literals;
 * share-target.test.ts asserts they stay in sync.
 */
export const SHARE_TARGET_PATH = '/share-target';
export const SHARED_FILE_CACHE = 'syncport-shared';
export const SHARED_FILE_URL = '/shared-file';
export const SHARED_QUERY_PARAM = 'shared';
export const SHARED_PAYLOAD_PARAM = 'payload';
export const SHARED_FILE_NAME_HEADER = 'X-File-Name';

export type ShareIntent = Readonly<{ kind: 'file' }> | Readonly<{ kind: 'text'; payload: string }>;

/** Reads the redirect the worker issued after a share, if any. */
export const parseShareQuery = (search: string): ShareIntent | null => {
  const params = new URLSearchParams(search);
  switch (params.get(SHARED_QUERY_PARAM)) {
    case 'file':
      return { kind: 'file' };
    case 'text':
      return { kind: 'text', payload: params.get(SHARED_PAYLOAD_PARAM) ?? '' };
    default:
      return null;
  }
};

/** Finds a receive route inside shared text: a link to this origin's /r/<id>, or a bare PIN. */
export const receivePathFromSharedText = (text: string, origin: string): string | null => {
  const link = new RegExp(`${origin.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}/r/([a-z0-9]+)`).exec(text);
  if (link !== null && link[1] !== undefined) {
    return `/r/${link[1]}`;
  }
  const pin = new RegExp(`(?<![0-9])([0-9]{${PIN_LENGTH}})(?![0-9])`).exec(text.replace(/(\d)[\s-](?=\d)/g, '$1'));
  if (pin !== null && pin[1] !== undefined) {
    return buildPinPath(pin[1]);
  }
  return null;
};

/** Retrieves the file the worker stored for a share and removes it so a reload does not resend it. */
export const takeSharedFile = async (): Promise<File | null> => {
  const cache = await caches.open(SHARED_FILE_CACHE);
  const response = await cache.match(SHARED_FILE_URL);
  if (response === undefined) {
    return null;
  }
  await cache.delete(SHARED_FILE_URL);
  const name = decodeURIComponent(response.headers.get(SHARED_FILE_NAME_HEADER) ?? 'shared-file');
  const type = response.headers.get('Content-Type') ?? 'application/octet-stream';
  return new File([await response.blob()], name, { type });
};

/** Removes the share parameters from the address bar without reloading. */
export const clearShareQuery = (): void => {
  window.history.replaceState(null, '', window.location.pathname);
};
